import React, { Dispatch, SetStateAction } from "react";
import { useQueryClient, useMutation } from "react-query";
import * as mutation from "src/api/affirmation/intentions/mutations";
import { QUERY_KEYS } from "./constants";
import { put } from "src/api";
import { useToast } from "@chakra-ui/react";

/**
 * @deprecated This hook is deprecated. Use useMessageContext from MessageContext instead.
 * Custom hook for managing message actions and state
 */
export const useMessageActions = () => {
  const toast = useToast();

  // State to track accepted messages (mocked for now)
  const [acceptedMessages, setAcceptedMessages] = React.useState<Set<string>>(
    new Set()
  );

  const queryClient = useQueryClient();

  // Mutation for deleting intentions
  const { mutateAsync: deleteIntentionMutation } = useMutation(
    mutation.deleteIntention,
    {
      onSuccess: () => queryClient.invalidateQueries(QUERY_KEYS.LIST_INTENTION),
    }
  );

  // Mutation for deleting subliminals
  const { mutateAsync: deleteSubliminalMutation } = useMutation(
    mutation.deleteSubliminal,
    {
      onSuccess: () =>
        queryClient.invalidateQueries(QUERY_KEYS.LIST_SUBLIMINAL),
    }
  );

  // Handler for accepting a message (dummy implementation)
  const handleAccept = React.useCallback(
    async (
      sharedId: number,
      intentionId: string,
      setIsLoading: Dispatch<SetStateAction<boolean>>,
      setIsAcceptingLoading: Dispatch<SetStateAction<boolean>>
    ) => {
      setIsLoading(true);
      setIsAcceptingLoading(true);
      const response = await put(`/shared/${sharedId}/approve`, { sharedId });
      setIsLoading(false);
      setIsAcceptingLoading(false);
      if (response.status === 200) {
        setAcceptedMessages((prev) =>
          new Set(prev).add(intentionId.toString())
        );
      } else {
        // show an error toast
        // toast({
        //   title: "Error",
        //   description: "Failed to accept message",
        //   status: "error",
        // });
      }
    },
    []
  );

  // Handler for declining a message (dummy implementation)
  const handleDecline = React.useCallback(
    async (
      sharedId: number,
      intentionId: string,
      setIsLoading: Dispatch<SetStateAction<boolean>>,
      setIsDecliningLoading: Dispatch<SetStateAction<boolean>>
    ) => {
      setIsLoading(true);
      setIsDecliningLoading(true);
      const response = await put(`/shared/${sharedId}/reject`, { sharedId });
      setIsLoading(false);
      setIsDecliningLoading(false);
    },
    []
  );

  // Handler for deleting a message
  const handleDelete = React.useCallback(
    (intentionId: string, isSubliminal: boolean) => {
      const deleteMutation = isSubliminal
        ? deleteSubliminalMutation
        : deleteIntentionMutation;

      // Convert string ID to number as expected by the API
      const numericId = Number(intentionId);
      return deleteMutation(numericId);
    },
    [deleteIntentionMutation, deleteSubliminalMutation]
  );

  // Check if a message is accepted
  const isMessageAccepted = React.useCallback(
    (intentionId: string) => acceptedMessages.has(intentionId),
    [acceptedMessages]
  );

  return {
    acceptedMessages,
    handleAccept,
    handleDecline,
    handleDelete,
    isMessageAccepted,
  };
};

// Re-export context hooks for easier migration
export {
  useMessageContext,
  useMessageActions as useMessageActionsContext,
  useIntentionLoadingStates,
} from "./MessageContext";
